/**
 * Admin install-stats RPC definition.
 *
 *   - `admin/installs/stats` — the latest box vitals and OpenClaw stats for
 *     one install, read from `agent_installation_stats`, alongside the server
 *     receive time of each report. A box that has never reported yields an
 *     all-null payload rather than an error.
 *
 * Admin-only (see {@link AdminMiddleware}).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OcStatsReport, SystemVitals } from "@fm/db/models/AgentInstallationStats";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Wire schemas
// ============================================================================

/**
 * Input for `admin/installs/stats`.
 *
 * @since 1.0.0
 */
export const InstallStatsInput = Schema.Struct({ installationId: Schema.String });
export type InstallStatsInput = Schema.Schema.Type<typeof InstallStatsInput>;

/**
 * The latest stats for one install. Each report is `null` until the box first
 * sends it; its `*ObservedAt` is the server's receive time of that report.
 *
 * @since 1.0.0
 */
export const InstallStats = Schema.Struct({
  installationId: Schema.String,
  systemVitals: Schema.NullOr(SystemVitals),
  systemVitalsObservedAt: Schema.NullOr(Schema.DateTimeUtc),
  ocStats: Schema.NullOr(OcStatsReport),
  ocStatsObservedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type InstallStats = Schema.Schema.Type<typeof InstallStats>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Install-Stats RPC group. Tags are namespaced under
 * `admin/installs/`.
 *
 * @since 1.0.0
 */
export class InstallStatsRpcs extends RpcGroup.make(
  Rpc.make("stats", {
    payload: InstallStatsInput,
    success: InstallStats,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
).prefix("admin/installs/") {}
